import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import PageHero from "../../components/public/PageHero";
import { Linkedin, Mail, User } from "lucide-react";

const GROUP_LABELS = {
  honor: { es: "Presidencia de Honor", fr: "Présidence d'Honneur" },
  counselors: { es: "Consejeros Económicos y Comerciales", fr: "Conseillers Économiques et Commerciaux" },
  board: { es: "Junta Directiva", fr: "Conseil d'Administration" },
  team: { es: "Equipo Profesional", fr: "Équipe Professionnelle" },
};

const GROUP_ORDER = ["honor", "counselors", "board", "team"];

function PersonCard({ p, lang, big }) {
  const role = p[`role_${lang}`] || p.role_es;
  return (
    <article
      data-testid={`person-${p.id}`}
      className={`bg-white border border-gray-100 group hover:border-[#0F2040] transition-all ${big ? "p-7" : "p-6"}`}
    >
      <div className={`relative overflow-hidden bg-gray-50 mb-5 ${big ? "aspect-[4/5]" : "aspect-square"}`}>
        {p.photo_url ? (
          <img
            src={p.photo_url}
            alt={p.name}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
            style={{ objectPosition: `${p.focal_x ?? 50}% ${p.focal_y ?? 50}%` }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F2040] to-[#1A2F5C] text-cecit-gold">
            <User className="w-14 h-14 opacity-40" strokeWidth={1.2} />
          </div>
        )}
      </div>
      <h4 className={`font-serif text-[#0F2040] ${big ? "text-2xl" : "text-xl"} leading-tight mb-1`}>
        {p.name}
      </h4>
      <p className="text-xs uppercase tracking-[0.18em] text-[#C8102E] mb-4 leading-snug">
        {role}
      </p>
      {p.bio_es && lang === "es" && <p className="text-sm text-gray-600 leading-relaxed mb-4">{p.bio_es}</p>}
      {p.bio_fr && lang === "fr" && <p className="text-sm text-gray-600 leading-relaxed mb-4">{p.bio_fr}</p>}
      {(p.email || p.linkedin) && (
        <div className="flex gap-2 pt-4 border-t border-gray-100">
          {p.email && (
            <a
              href={`mailto:${p.email}`}
              data-testid={`person-email-${p.id}`}
              className="w-9 h-9 border border-gray-200 hover:border-[#0F2040] hover:bg-[#0F2040] hover:text-white text-gray-600 flex items-center justify-center transition-colors"
              aria-label="Email"
            >
              <Mail className="w-4 h-4" />
            </a>
          )}
          {p.linkedin && (
            <a
              href={p.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid={`person-linkedin-${p.id}`}
              className="w-9 h-9 border border-gray-200 hover:border-[#0077B5] hover:bg-[#0077B5] hover:text-white text-gray-600 flex items-center justify-center transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          )}
        </div>
      )}
    </article>
  );
}

export default function LaCamara() {
  const { lang, t, pick } = useLang();
  const [page, setPage] = useState(null);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/pages/la-camara").then((r) => setPage(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  if (!page) return <div className="min-h-[50vh] flex items-center justify-center text-gray-400">…</div>;

  const grouped = GROUP_ORDER.map((g) => ({ key: g, items: team.filter((m) => m.group === g) })).filter((g) => g.items.length > 0);

  return (
    <div data-testid="la-camara-page">
      <PageHero title={pick(page, "title")} subtitle={pick(page, "subtitle")} image={page.hero_image} />

      <article className="max-w-3xl mx-auto px-6 py-20">
        {(() => {
          const body = pick(page, "body");
          const isHTML = /<[a-z][\s\S]*>/i.test(body);
          if (isHTML) {
            return <div className="prose-cecit text-gray-700 text-lg" dangerouslySetInnerHTML={{ __html: body }} />;
          }
          return (
            <div className="prose-cecit text-gray-700 leading-relaxed text-lg">
              {body.split(/\n\n+/).map((para, i) => {
                if (para.startsWith("## ")) {
                  return <h2 key={i}>{para.slice(3)}</h2>;
                }
                return <p key={i}>{para}</p>;
              })}
            </div>
          );
        })()}
      </article>

      {grouped.length > 0 && (
        <section className="bg-white border-t border-gray-100 py-20" id="equipo" data-testid="team-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8102E] mb-3">
                <span className="editorial-rule mr-3 align-middle"></span>
                {lang === "es" ? "Quiénes hacen la CECIT" : "Ceux qui font la CECIT"}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0F2040]">
                {lang === "es" ? "Junta Directiva y Equipo" : "Conseil et Équipe"}
              </h2>
            </div>

            {grouped.map((g, idx) => {
              const isHonor = g.key === "honor";
              const isLarge = isHonor || g.key === "counselors";
              return (
                <div key={g.key} className={idx > 0 ? "mt-16" : ""}>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="editorial-rule"></span>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#0F2040]" data-testid={`team-group-${g.key}`}>
                      {GROUP_LABELS[g.key][lang]}
                    </h3>
                  </div>
                  <div
                    className={`grid gap-6 ${
                      isLarge
                        ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                        : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                    }`}
                  >
                    {g.items.map((p) => (
                      <PersonCard key={p.id} p={p} lang={lang} big={isLarge} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
